import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { restoreFamily } from '../services/apiService';
import { useLanguage } from '../context/LanguageContext';
import { i18n } from '../i18n/i18n';
import { KidButton } from './kid/KidUI';
import { showToast } from './kid/Toast';
import AntMascot from './AntMascot';
import { daysLeft, formatDeletionDate } from '../utils/accountDeletion';

/**
 * Shown instead of the app while the whole family is waiting to be deleted (account deletion,
 * 2026-10-12). Nothing else works until an admin restores it; after 30 days it is gone for good.
 */
const FamilyDeletionPending: React.FC = () => {
  const { current_user, logout, refreshUserData, refreshFamilyData } = useAppContext() as any;
  const { current_language } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const deletion = current_user?.family_deletion;
  if (!deletion) return null;

  const restore = async () => {
    setBusy(true);
    setError('');
    try {
      await restoreFamily();
      await refreshUserData();
      await refreshFamilyData();
      showToast({ message: i18n.t('deletion.family_restored_toast') });
    } catch (e: any) {
      setError(e?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="min-h-screen w-full flex items-center justify-center px-4 py-8 bg-kid-page dark:bg-kid-bg-dark text-kid-text dark:text-kid-text-dark"
      lang={current_language}
    >
      <div className="w-full max-w-md rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark p-6 space-y-4 text-center">
        <AntMascot className="w-24 h-24 mx-auto" />
        <h1 className="font-kid-heading font-extrabold text-2xl">
          {i18n.t('deletion.pending_title').replace('{{family}}', deletion.family_name)}
        </h1>
        <p className="text-base">
          {i18n.t('deletion.pending_text').replace('{{date}}', formatDeletionDate(deletion.permanent_at, current_language))}
        </p>
        <p className="font-bold">{i18n.t('deletion.days_left').replace('{{days}}', String(daysLeft(deletion.permanent_at)))}</p>
        {!current_user.is_admin && <p className="text-base">{i18n.t('deletion.pending_not_admin')}</p>}
        {error && <p role="alert" className="font-bold text-red-700 dark:text-red-300">{error}</p>}
        <div className="flex flex-col gap-3 pt-2">
          {current_user.is_admin && (
            <KidButton size="md" disabled={busy} onClick={restore}>
              {busy ? '…' : i18n.t('deletion.restore_family')}
            </KidButton>
          )}
          <KidButton size="md" variant="secondary" disabled={busy} onClick={() => logout()}>
            {i18n.t('deletion.log_out')}
          </KidButton>
        </div>
      </div>
    </main>
  );
};

export default FamilyDeletionPending;
